import { z } from 'zod';

/** Aceita qualquer valor bruto do input; `z.number()` rejeita `NaN` antes do transform. */
export const numberInputValue = z.unknown();

export function parseRequiredInt(
  val: unknown,
  ctx: z.RefinementCtx,
  config: {
    requiredMessage: string;
    min: number;
    max?: number;
    minMessage?: string;
    maxMessage?: string;
  },
): number {
  if (val === '' || val === null || val === undefined) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, message: config.requiredMessage });
    return z.NEVER;
  }

  const num = typeof val === 'number' ? val : Number(val);
  if (Number.isNaN(num)) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, message: config.requiredMessage });
    return z.NEVER;
  }
  if (!Number.isInteger(num)) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, message: config.requiredMessage });
    return z.NEVER;
  }
  if (num < config.min) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: config.minMessage ?? `Mínimo ${config.min}`,
    });
    return z.NEVER;
  }
  if (config.max != null && num > config.max) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: config.maxMessage ?? `Máximo ${config.max}`,
    });
    return z.NEVER;
  }
  return num;
}

export function requiredIntFromNumberInput(config: {
  requiredMessage: string;
  min: number;
  max?: number;
  minMessage?: string;
  maxMessage?: string;
}) {
  return numberInputValue.transform((val, ctx) => parseRequiredInt(val, ctx, config));
}

export function parseRequiredReferenceId(val: unknown, ctx: z.RefinementCtx): number {
  if (val === '' || val === null || val === undefined) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Selecione uma opção' });
    return z.NEVER;
  }
  const num = typeof val === 'number' ? val : Number(val);
  if (!Number.isInteger(num) || num <= 0) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Selecione uma opção' });
    return z.NEVER;
  }
  return num;
}

/** ID de referência — espelha `{ id: number }` dos DTOs da API. */
export const requiredReferenceIdSchema = z.object({
  id: numberInputValue.transform((val, ctx) => parseRequiredReferenceId(val, ctx)),
});

export const optionalReferenceIdSchema = z
  .object({
    id: z.number().int().positive(),
    nome: z.string().nullish().optional(),
  })
  .optional();

/** Normaliza referência { id, nome } da API para o formulário (Zod não aceita `null` em nome). */
export function normalizeOptionalReference(
  ref?: { id?: number; nome?: string | null } | null,
): { id: number; nome: string } | undefined {
  if (!ref?.id || Number(ref.id) <= 0) return undefined;
  return { id: Number(ref.id), nome: ref.nome == null ? '' : String(ref.nome) };
}

function parseOptionalDdd(val: unknown, ctx: z.RefinementCtx): number | undefined {
  if (val === '' || val === null || val === undefined) return undefined;
  const num = typeof val === 'number' ? val : Number(val);
  if (Number.isNaN(num)) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'DDD inválido' });
    return z.NEVER;
  }
  if (!Number.isInteger(num) || num < 10 || num > 99) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'DDD inválido' });
    return z.NEVER;
  }
  return num;
}

export const telefoneFormSchema = z
  .object({
    id: z.number().optional(),
    ddd: numberInputValue.transform((val, ctx) => parseOptionalDdd(val, ctx)).optional(),
    numero: z.preprocess((v) => (v == null ? '' : v), z.string().optional()),
  })
  .optional()
  .superRefine((telefone, ctx) => {
    const numero = telefone?.numero?.replace(/\D/g, '') ?? '';
    if (numero.length > 0 && telefone?.ddd == null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Informe o DDD',
        path: ['ddd'],
      });
    }
  });

/** Telefone obrigatório (ex.: instituição de ensino). */
export const telefoneRequiredFormSchema = z
  .object({
    ddd: numberInputValue.transform((val, ctx) => parseOptionalDdd(val, ctx)),
    numero: z.string().optional(),
  })
  .superRefine((telefone, ctx) => {
    if (telefone.ddd == null) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Informe o DDD', path: ['ddd'] });
    }
    const numero = telefone.numero?.replace(/\D/g, '') ?? '';
    if (numero.length === 0) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Informe o telefone', path: ['numero'] });
    }
  });

export const enderecoFormSchema = z.object({
  cep: z.string().trim().min(1, 'Informe o CEP'),
  cidade: z.string().trim().min(1, 'Informe a cidade'),
  bairro: z.string().trim().optional(),
  logradouro: z.string().trim().min(1, 'Informe o logradouro'),
  complemento: z.string().trim().optional(),
  numero: numberInputValue
    .transform((val, ctx) => {
      if (val === '' || val === null || val === undefined) return undefined;
      const num = typeof val === 'number' ? val : Number(val);
      if (Number.isNaN(num)) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Número inválido' });
        return z.NEVER;
      }
      return num;
    })
    .optional(),
});
